import React from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import WorkIcon from '@mui/icons-material/FolderOutlined';
import DashboardCustomizeIcon from '@mui/icons-material/ViewKanbanOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { SectionLabel, useModeTokens } from '@/common/components/ui';
import { AppDrawer, useAppDrawerState } from './AppDrawer';

export { DRAWER_WIDTH } from './AppDrawer';
export const DRAWER_COLLAPSED_WIDTH = 72;

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const NAV_ITEMS: { label: string; path: string; icon: React.ReactNode }[] = [
  { label: 'Projects', path: '/projects', icon: <WorkIcon /> },
  { label: 'Blueprints', path: '/blueprints', icon: <GraphicEqIcon /> },
  { label: 'Boards', path: '/boards', icon: <DashboardCustomizeIcon /> },
];

interface NavItemProps {
  label: string;
  path: string;
  icon: React.ReactNode;
  active: boolean;
  collapsed: boolean;
  transition: string;
  onNavigate: (path: string) => void;
}

/**
 * One nav row. Shape, tint and hover come from the `MuiListItemButton`
 * override — only the things that depend on the collapsed state live here.
 */
const NavItem: React.FC<NavItemProps> = ({
  label,
  path,
  icon,
  active,
  collapsed,
  transition,
  onNavigate,
}) => {
  const t = useModeTokens();

  return (
    <ListItemButton
      selected={active}
      onClick={() => onNavigate(path)}
      sx={{
        minHeight: 40,
        px: collapsed ? 1.5 : 1.25,
        justifyContent: collapsed ? 'center' : 'flex-start',
        transition: `padding ${transition}, justify-content ${transition}`,
      }}
    >
      {/* Inactive rows keep a transparent rail so every icon and label stays on
          the same optical left edge. Collapsed, there is no room for it and the
          tinted background carries the active state on its own. */}
      {!collapsed && (
        <Box
          aria-hidden
          sx={{
            width: 3,
            height: 14,
            flex: 'none',
            marginRight: '10px',
            borderRadius: '3px',
            background: active ? t.colors.accentFill : 'transparent',
          }}
        />
      )}
      <ListItemIcon
        sx={{
          minWidth: collapsed ? 0 : 22,
          '& svg': { fontSize: 19 },
          mr: collapsed ? 0 : 1,
          justifyContent: 'center',
          transition: `min-width ${transition}, margin-right ${transition}`,
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={label}
        slotProps={{
          primary: { fontWeight: active ? 600 : 400, fontSize: 13 },
        }}
        sx={{
          opacity: collapsed ? 0 : 1,
          maxWidth: collapsed ? 0 : 160,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          transition: `opacity ${transition}, max-width ${transition}`,
        }}
      />
    </ListItemButton>
  );
};

// Inner component so it can consume AppDrawerContext (provided by AppDrawer above it in the tree)
const SidebarNav: React.FC<{ isSmallScreen: boolean; onClose: () => void }> = ({
  isSmallScreen,
  onClose,
}) => {
  const { showMiniCollapsed, sidebarTransition } = useAppDrawerState();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useModeTokens();

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isSmallScreen) {
      onClose();
    }
  };

  return (
    <>
      <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: showMiniCollapsed ? 'center' : 'flex-start',
          }}
        >
          <SectionLabel size={10} color={t.text.faint}>
            {showMiniCollapsed ? 'NAV' : 'NAVIGATION'}
          </SectionLabel>
        </Box>
        <List>
          {NAV_ITEMS.map((item) => (
            <NavItem
              key={item.path}
              {...item}
              active={
                location.pathname === item.path ||
                location.pathname.startsWith(`${item.path}/`)
              }
              collapsed={showMiniCollapsed}
              transition={sidebarTransition}
              onNavigate={handleNavigate}
            />
          ))}
        </List>
      </Box>

      {/* Version pinned at bottom */}
      <Box
        sx={{
          p: showMiniCollapsed ? 0 : 2,
          borderTop: '1px solid',
          borderColor: showMiniCollapsed ? 'transparent' : t.borders.strong,
          opacity: showMiniCollapsed ? 0 : 1,
          maxHeight: showMiniCollapsed ? 0 : 90,
          overflow: 'hidden',
          transition: `opacity ${sidebarTransition}, max-height ${sidebarTransition}, padding ${sidebarTransition}, border-color ${sidebarTransition}`,
        }}
      >
        <Typography variant="caption" color="text.disabled" display="block">
          Studio Vault v{__APP_VERSION__}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          BETA
        </Typography>
      </Box>
    </>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <AppDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      collapsedWidth={DRAWER_COLLAPSED_WIDTH}
    >
      <SidebarNav isSmallScreen={isSmallScreen} onClose={onClose} />
    </AppDrawer>
  );
};
